import { Tone } from '../types';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import TodayIcon from '@mui/icons-material/Today';
import InboxIcon from '@mui/icons-material/Inbox';
import GroupsIcon from '@mui/icons-material/Groups';
import { FreshdeskDashboardResponse } from 'library/types';
import { FRESHDESK_TICKET_FILTER_URLS } from '../constants';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

const useFreshdeskMetrics = (dashboard: FreshdeskDashboardResponse | null) => {
  const intl = useIntl();

  return useMemo(
    () => [
      {
        icon: <ConfirmationNumberIcon />,
        title: intl.formatMessage({ id: 'freshdesk.metrics.todayOpenPending' }),
        value: dashboard?.todayOpenPendingTicketCount || 0,
        helper: intl.formatMessage({ id: 'freshdesk.metrics.todayOpenPendingHelper' }),
        tone: 'primary' as Tone,
        href: FRESHDESK_TICKET_FILTER_URLS.unresolved,
      },
      {
        icon: <GroupsIcon />,
        title: intl.formatMessage({ id: 'freshdesk.metrics.unassigned' }),
        value: dashboard?.unassignedOpenPendingTicketCount || 0,
        helper: intl.formatMessage({ id: 'freshdesk.metrics.unassignedHelper' }),
        tone: 'warning' as Tone,
        href: FRESHDESK_TICKET_FILTER_URLS.new,
      },
      {
        icon: <ErrorOutlineIcon />,
        title: intl.formatMessage({ id: 'freshdesk.metrics.overdue' }),
        value: dashboard?.overdueOpenPendingTicketCount || 0,
        helper: intl.formatMessage({ id: 'freshdesk.metrics.overdueHelper' }),
        tone: 'error' as Tone,
        href: FRESHDESK_TICKET_FILTER_URLS.overdue,
      },
      {
        icon: <TodayIcon />,
        title: intl.formatMessage({ id: 'freshdesk.metrics.todayDue' }),
        value: dashboard?.todayDueOpenPendingTicketCount || 0,
        helper: intl.formatMessage({ id: 'freshdesk.metrics.todayDueHelper' }),
        tone: 'info' as Tone,
        href: FRESHDESK_TICKET_FILTER_URLS.dueToday,
      },
      {
        icon: <SupportAgentIcon />,
        title: intl.formatMessage({ id: 'freshdesk.metrics.myOpenPending' }),
        value: dashboard?.currentAgentOpenPendingTicketCount || 0,
        helper: intl.formatMessage({ id: 'freshdesk.metrics.myOpenPendingHelper' }),
        tone: 'primary' as Tone,
        href: FRESHDESK_TICKET_FILTER_URLS.myOpenPending,
      },
      {
        icon: <InboxIcon />,
        title: intl.formatMessage({ id: 'freshdesk.metrics.todayCreated' }),
        value: dashboard?.todayCreatedTicketCount || 0,
        helper: intl.formatMessage({ id: 'freshdesk.metrics.todayCreatedHelper' }),
        tone: 'info' as Tone,
        href: FRESHDESK_TICKET_FILTER_URLS.todayCreated,
      },
      {
        icon: <CheckCircleIcon />,
        title: intl.formatMessage({ id: 'freshdesk.metrics.todayClosed' }),
        value: dashboard?.todayClosedTicketCount || 0,
        helper: intl.formatMessage({ id: 'freshdesk.metrics.todayClosedHelper' }),
        tone: 'success' as Tone,
        href: FRESHDESK_TICKET_FILTER_URLS.todayClosed,
      },
    ],
    [dashboard, intl]
  );
};

export default useFreshdeskMetrics;
